import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, Transition as _Transition, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "truncate" }
const _hoisted_2 = { class: "flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-50" }
const _hoisted_3 = { class: "min-w-0 flex-1" }
const _hoisted_4 = {
  key: 0,
  "data-test": "option-location",
  class: "block truncate text-xs text-gray-500"
}
const _hoisted_5 = { class: "flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100" }
const _hoisted_6 = {
  "data-test": "option-name",
  class: "min-w-0 flex-1 truncate text-sm font-medium text-gray-900"
}
const _hoisted_7 = ["src"]
const _hoisted_8 = {
  key: 1,
  "data-test": "option-initials",
  class: "flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-xs font-medium text-gray-600",
  "aria-hidden": "true"
}
const _hoisted_9 = { class: "min-w-0 flex-1" }
const _hoisted_10 = {
  "data-test": "option-name",
  class: "block truncate text-sm font-medium text-gray-900"
}
const _hoisted_11 = {
  key: 0,
  "data-test": "option-location",
  class: "block truncate text-xs text-gray-500"
}
const _hoisted_12 = {
  key: 3,
  class: "px-3 py-2 text-sm text-gray-500"
}

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import { BuildingStorefrontIcon, Squares2X2Icon } from '@heroicons/vue/24/outline'
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

import InputField from '@/components/neue/InputField.vue'
import { useDropdownAnchor } from '@/composables/useDropdownAnchor'
import { blueprintIdOf, isBlueprintId, merchantLocation, merchantName } from '@/helpers/franchiseMerchant'

const SEARCH_THRESHOLD = 8
const BLUEPRINT_LABEL = 'Franchise menu'
// Keys the listbox still needs from the search box: moving through, picking and closing.

export default {
  __name: 'FranchiseMerchantSwitcher',
  props: {
  allLabel: { type: String, default: 'All franchisees' },
  includeAll: { type: Boolean, default: true },
  // Offers the franchise menu (the blueprint) first; only the menu pages edit it. Before the franchise has
  // a menu the option stands for no location chosen, where those pages offer to set one up.
  includeBlueprint: { type: Boolean, default: false },
  // False leaves the switch to the page, for pages that must take their lists down first.
  switchStore: { type: Boolean, default: true }
},
  emits: ['change'],
  setup(__props, { emit: __emit }) {

const props = __props
// Tells the page which franchisee was chosen (null for all of them), once the store has switched to it
// unless switchStore is false.
const emit = __emit

const LISTBOX_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab']

const store = useStore()
const { setReference, setFloating, floatingStyles } = useDropdownAnchor({ placement: 'bottom-end' })

const query = ref('')

const merchants = computed(() => store.state.franchiseMerchants ?? [])
const storeActiveId = computed(() => store.state.activeFranchiseMerchantId ?? null)
const blueprintId = computed(() => blueprintIdOf(store.state))
const franchiseName = computed(() => store.state.franchise?.attributes?.name ?? '')
const blueprintActive = computed(() => isBlueprintId(storeActiveId.value, blueprintId.value))
// A page without the franchise menu reads it as all locations, so its switcher says so too.
const activeId = computed(() => (blueprintActive.value && !props.includeBlueprint ? null : storeActiveId.value))
const activeMerchant = computed(() => merchants.value.find(merchant => merchant.id === activeId.value))
const showsBlueprint = computed(
  () => props.includeBlueprint && (blueprintActive.value || (!blueprintId.value && !activeId.value))
)
const searchable = computed(() => merchants.value.length > SEARCH_THRESHOLD)

const buttonLabel = computed(() => {
  if (showsBlueprint.value) return BLUEPRINT_LABEL
  return merchantName(activeMerchant.value) || (props.includeAll ? props.allLabel : 'Choose a franchisee')
})

const matchesQuery = (merchant, needle) =>
  ['user_name', 'handle', 'city'].some(key => (merchant.attributes[key] ?? '').toLowerCase().includes(needle))

const needle = computed(() => query.value.trim().toLowerCase())

const visibleMerchants = computed(() => {
  if (!searchable.value || !needle.value) return merchants.value
  return merchants.value.filter(merchant => matchesQuery(merchant, needle.value))
})

const emptyMessage = computed(() => {
  if (visibleMerchants.value.length) return null
  return needle.value ? 'No franchisees match' : 'No franchisees yet'
})

// Franchisees often share a brand name, so the initials come from what sets them apart: the city first.
const initialsSource = merchant =>
  ['city', 'handle', 'user_name'].map(key => merchant.attributes[key]?.trim()).find(Boolean) ?? ''

const initials = merchant => {
  const words = initialsSource(merchant)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
  const letters = words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? '').slice(0, 2)
  return letters.toUpperCase()
}

const optionClasses = active => [
  active ? 'bg-gray-50' : '',
  'flex cursor-default select-none items-center gap-3 px-3 py-2'
]

// The listbox reads letters as typeahead and space as a pick, so typing in the search box stays in it.
const keepTypingInSearch = event => {
  if (!LISTBOX_KEYS.includes(event.key)) event.stopPropagation()
}

const choose = async id => {
  query.value = ''
  // Against the store, not the shown choice: All on a page without the franchise menu leaves it.
  if (id === storeActiveId.value) return
  if (props.switchStore) await store.dispatch('setActiveFranchiseMerchant', id)
  emit('change', id)
}

// Listbox only exposes whether it is open through its slot, so this watches it there.
const ClearOnClose = {
  props: { open: Boolean },
  emits: ['close'],
  setup(props, { emit }) {
    watch(
      () => props.open,
      open => {
        if (!open) emit('close')
      }
    )
    return () => null
  }
}

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(_unref(Listbox), {
    "model-value": activeId.value,
    "onUpdate:modelValue": choose
  }, {
    default: _withCtx(({ open }) => [
      _createVNode(ClearOnClose, {
        open: open,
        onClose: _cache[0] || (_cache[0] = $event => (query.value = ''))
      }, null, 8, ["open"]),
      _createVNode(_unref(ListboxButton), {
        ref: _unref(setReference),
        "aria-label": "Choose franchisee",
        class: "inline-flex min-w-0 max-w-[9rem] sm:max-w-xs items-center gap-2 rounded-full border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(BuildingStorefrontIcon), {
            class: "h-4 w-4 shrink-0 text-gray-400",
            "aria-hidden": "true"
          }),
          _createElementVNode("span", _hoisted_1, _toDisplayString(buttonLabel.value), 1),
          _createVNode(_unref(ChevronUpDownIcon), {
            class: "h-4 w-4 shrink-0 text-gray-400",
            "aria-hidden": "true"
          })
        ]),
        _: 1
      }, 512),
      (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
        _createVNode(_Transition, {
          "leave-active-class": "transition ease-in duration-100",
          "leave-from-class": "opacity-100",
          "leave-to-class": "opacity-0"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ListboxOptions), {
              ref: _unref(setFloating),
              style: _normalizeStyle(_unref(floatingStyles)),
              as: "div",
              class: "z-50 max-h-80 w-72 overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
            }, {
              default: _withCtx(() => [
                (searchable.value)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: "px-2 pt-1 pb-2",
                      onKeydown: keepTypingInSearch
                    }, [
                      _createVNode(InputField, {
                        modelValue: query.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((query).value = $event)),
                        placeholder: "Search franchisees",
                        "aria-label": "Search franchisees"
                      }, null, 8, ["modelValue"])
                    ], 32))
                  : _createCommentVNode("", true),
                (__props.includeBlueprint)
                  ? (_openBlock(), _createBlock(_unref(ListboxOption), {
                      key: 1,
                      value: blueprintId.value,
                      as: "template"
                    }, {
                      default: _withCtx(({ active, selected }) => [
                        _createElementVNode("div", {
                          "data-test": "option-blueprint",
                          class: _normalizeClass(optionClasses(active))
                        }, [
                          _createElementVNode("span", _hoisted_2, [
                            _createVNode(_unref(Squares2X2Icon), {
                              class: "h-4 w-4 text-brand-600",
                              "aria-hidden": "true"
                            })
                          ]),
                          _createElementVNode("span", _hoisted_3, [
                            _createElementVNode("span", {
                              "data-test": "option-name",
                              class: "block truncate text-sm font-medium text-gray-900"
                            }, _toDisplayString(BLUEPRINT_LABEL)),
                            (franchiseName.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(franchiseName.value), 1))
                              : _createCommentVNode("", true)
                          ]),
                          selected
                            ? (_openBlock(), _createBlock(_unref(CheckIcon), {
                                key: 0,
                                class: "h-5 w-5 shrink-0 text-brand-600",
                                "aria-hidden": "true"
                              }))
                            : _createCommentVNode("", true)
                        ], 2)
                      ]),
                      _: 1
                    }, 8, ["value"]))
                  : _createCommentVNode("", true),
                (__props.includeAll)
                  ? (_openBlock(), _createBlock(_unref(ListboxOption), {
                      key: 2,
                      value: null,
                      as: "template"
                    }, {
                      default: _withCtx(({ active, selected }) => [
                        _createElementVNode("div", {
                          class: _normalizeClass(optionClasses(active))
                        }, [
                          _createElementVNode("span", _hoisted_5, [
                            _createVNode(_unref(Squares2X2Icon), {
                              class: "h-4 w-4 text-gray-500",
                              "aria-hidden": "true"
                            })
                          ]),
                          _createElementVNode("span", _hoisted_6, _toDisplayString(__props.allLabel), 1),
                          selected
                            ? (_openBlock(), _createBlock(_unref(CheckIcon), {
                                key: 0,
                                class: "h-5 w-5 shrink-0 text-brand-600",
                                "aria-hidden": "true"
                              }))
                            : _createCommentVNode("", true)
                        ], 2)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleMerchants.value, (merchant) => {
                  return (_openBlock(), _createBlock(_unref(ListboxOption), {
                    key: merchant.id,
                    value: merchant.id,
                    as: "template"
                  }, {
                    default: _withCtx(({ active, selected }) => [
                      _createElementVNode("div", {
                        class: _normalizeClass(optionClasses(active))
                      }, [
                        (merchant.attributes.avatar_url)
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: merchant.attributes.avatar_url,
                              alt: "",
                              class: "h-7 w-7 shrink-0 rounded-md object-cover"
                            }, null, 8, _hoisted_7))
                          : (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(initials(merchant)), 1)),
                        _createElementVNode("span", _hoisted_9, [
                          _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(merchantName)(merchant)), 1),
                          (_unref(merchantLocation)(merchant))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(_unref(merchantLocation)(merchant)), 1))
                            : _createCommentVNode("", true)
                        ]),
                        selected
                          ? (_openBlock(), _createBlock(_unref(CheckIcon), {
                              key: 2,
                              class: "h-5 w-5 shrink-0 text-brand-600",
                              "aria-hidden": "true"
                            }))
                          : _createCommentVNode("", true)
                      ], 2)
                    ]),
                    _: 2
                  }, 1032, ["value"]))
                }), 128)),
                (emptyMessage.value)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(emptyMessage.value), 1))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["style"])
          ]),
          _: 1
        })
      ]))
    ]),
    _: 1
  }, 8, ["model-value"]))
}
}

}